import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { isEmpty } from 'lodash';
import { takeLatest, put } from 'redux-saga/effects';
import { RolesApi } from 'data/roles';
import { queryClient } from 'queries';
import {
  MEMBERS_V2_MODIFIED_QUERY_KEY,
  MEMBERS_V2_QUERY_KEY,
} from 'queries/members';
import { inviteMembersAction, InviteOrUpdateMembersPayload } from '../actions';
import { InvitedUser, MembersAPI } from '../api';
import { addMembers } from '../slice';

function* inviteMembers(payload, handleSuccess, handleFailure) {
  const users = (yield MembersAPI.inviteMembers(payload)) as InvitedUser[];
  const invitedusers = users.filter((u) => u.status);
  const unInvitedUsers = users.filter((u) => !u.status);

  const allSuccess =
    !isEmpty(invitedusers) && invitedusers.length === users.length;
  const allFailure =
    !isEmpty(unInvitedUsers) && unInvitedUsers.length === users.length;
  const partialSuccess =
    !isEmpty(invitedusers) &&
    !isEmpty(unInvitedUsers) &&
    invitedusers.length !== users.length;

  if (allSuccess) {
    yield put(addMembers(invitedusers));
    if (handleSuccess) {
      handleSuccess();
    }

    yield queryClient.refetchQueries(MEMBERS_V2_QUERY_KEY);
    yield queryClient.refetchQueries(MEMBERS_V2_MODIFIED_QUERY_KEY);

    return true;
  }

  if (allFailure) {
    if (handleFailure) {
      handleFailure();
    }

    return false;
  }

  if (partialSuccess) {
    yield put(addMembers(invitedusers));

    if (handleFailure) {
      handleFailure(unInvitedUsers);
    }

    return false;
  }

  return false;
}

function* inviteMembersSaga(
  action: PayloadAction<InviteOrUpdateMembersPayload>,
) {
  const {
    payload: {
      data,
      role,
      handleAlways = () => {},
      handleSuccess,
      handleFailure = () => {},
    },
  } = action;

  try {
    let payload = data;

    if (role.isNew) {
      try {
        const createdRole = yield RolesApi.createWithDefaultPlaceholder({
          roleName: role.value,
        });

        payload = data.map((member) => {
          return {
            ...member,
            user: {
              ...member.user,
              role: createdRole,
              defaultCapacity: member.user.defaultCapacity,
              capacityMinutes: member.user.capacityMinutes,
            },
          };
        });

        yield inviteMembers(payload, handleSuccess, handleFailure);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        message.error('Creating a new role failed');

        handleFailure();
      }
    } else {
      yield inviteMembers(payload, handleSuccess, handleFailure);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    handleFailure();
  }

  handleAlways();
}

export const watchInviteMembersSaga = function* (): Generator {
  yield takeLatest(inviteMembersAction.type, inviteMembersSaga);
};
